import React, { useState } from 'react';

interface CalendarProps {
  startDate: string;
  endDate: string;
  onDateRangeSelect: (startDate: string, endDate: string) => void;
  minDate?: string;
  maxDate?: string;
  occupiedDates?: string[]; // Nouvelle prop pour les jours occupés
  onInvalidRange?: (message: string) => void; // Callback pour les plages invalides
}

const Calendar: React.FC<CalendarProps> = ({ 
  startDate, 
  endDate, 
  onDateRangeSelect, 
  minDate = new Date().toISOString().split('T')[0],
  maxDate,
  occupiedDates = [], // Par défaut, tableau vide
  onInvalidRange
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const getPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const getNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateOccupied = (date: string) => {
    return occupiedDates.includes(date);
  };

  // Fonction pour vérifier si une plage de dates chevauche des réservations
  const isDateRangeValid = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return true;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Vérifier si la plage sélectionnée chevauche des dates occupées
    return !occupiedDates.some(occupiedDate => {
      const occupied = new Date(occupiedDate);
      return occupied >= start && occupied <= end;
    });
  };

  const isDateDisabled = (date: string) => {
    const currentDate = new Date();
    const selectedDateObj = new Date(date);
    
    // Désactiver les dates passées
    if (selectedDateObj < new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())) {
      return true;
    }
    
    // Désactiver les dates après maxDate si définie
    if (maxDate && date > maxDate) {
      return true;
    }
    
    // Désactiver les dates occupées
    if (isDateOccupied(date)) {
      return true;
    }
    
    return false;
  };

  const isDateInRange = (date: string) => {
    if (!startDate || !endDate) return false;
    const dateObj = new Date(date);
    const startObj = new Date(startDate);
    const endObj = new Date(endDate);
    return dateObj >= startObj && dateObj <= endObj;
  };

  const isSingleDay = () => {
    // Jour unique = soit startDate === endDate, soit seulement startDate défini
    return (startDate && endDate && startDate === endDate) || (startDate && !endDate);
  };

  const isDateStart = (date: string) => {
    return date === startDate;
  };

  const isDateEnd = (date: string) => {
    return endDate && date === endDate && endDate !== startDate;
  };

  const isDateInHoverRange = (date: string) => {
    if (!startDate || !hoveredDate) return false;
    const dateObj = new Date(date);
    const startObj = new Date(startDate);
    const hoverObj = new Date(hoveredDate);
    
    if (startObj <= hoverObj) {
      return dateObj >= startObj && dateObj <= hoverObj;
    } else {
      return dateObj >= hoverObj && dateObj <= startObj;
    }
  };

  const handleDateClick = (date: string) => {
    if (isDateDisabled(date)) return;

    console.log('Calendar click:', { date, startDate, endDate }); // Debug

    // Si on clique sur la date de début existante ET qu'on a une plage complète, on recommence
    if (date === startDate && endDate && startDate !== endDate) {
      console.log('Clicking on start date with full range - restarting selection');
      onDateRangeSelect(date, '');
      return;
    }

    // Si on clique sur la date de fin existante, on recommence la sélection
    if (date === endDate && startDate !== endDate) {
      console.log('Clicking on end date - restarting selection');
      onDateRangeSelect(date, '');
      return;
    }

    // Si on n'a pas de date de début, on commence une nouvelle sélection
    if (!startDate) {
      console.log('No start date - starting new selection');
      onDateRangeSelect(date, '');
      return;
    }

    // Si on a une plage complète (début + fin), on commence une nouvelle sélection
    if (startDate && endDate) {
      console.log('Full range selected - starting new selection');
      onDateRangeSelect(date, '');
      return;
    }

    // Deuxième sélection - créer une plage
    console.log('Second selection - creating range');
    const startObj = new Date(startDate);
    const endObj = new Date(date);
    
    // Si on clique sur la même date que le début, on crée un jour unique
    if (date === startDate) {
      console.log('Same date clicked - creating single day');
      onDateRangeSelect(startDate, startDate);
      return;
    }
    
    if (endObj >= startObj) {
      // Créer la plage (la validation sera faite côté Booking)
      onDateRangeSelect(startDate, date);
    } else {
      // Créer la plage dans l'ordre inverse (la validation sera faite côté Booking)
      onDateRangeSelect(date, startDate);
    }
  };

  const handleDateHover = (date: string) => {
    if (startDate && !endDate && !isDateDisabled(date)) {
      setHoveredDate(date);
    }
  };

  const handleMouseLeave = () => {
    setHoveredDate(null);
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const daysOfWeek = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 w-full">
      {/* Header du calendrier */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={getPreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        
        <h3 className="text-lg font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          onClick={getNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>

      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek.map((day) => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Grille des jours */}
      <div className="grid grid-cols-7 gap-1 w-full" onMouseLeave={handleMouseLeave}>
        {/* Jours vides au début */}
        {Array.from({ length: startingDay }, (_, index) => (
          <div key={`empty-${index}`} className="h-12"></div>
        ))}
        
        {/* Jours du mois */}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1;
          const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
          const dateString = formatDate(date);
          const isDisabled = isDateDisabled(dateString);
          const isOccupied = isDateOccupied(dateString);
          const isStart = isDateStart(dateString);
          const isEnd = isDateEnd(dateString);
          const isInRange = isDateInRange(dateString);
          const isInHoverRange = isDateInHoverRange(dateString);
          
          let className = "h-12 rounded-lg text-sm font-medium transition-colors relative flex items-center justify-center";
          
          if (isDisabled) {
            if (isOccupied) {
              className += " bg-orange-100 text-orange-600 cursor-not-allowed";
            } else {
              className += " text-gray-300 cursor-not-allowed";
            }
          } else if (isStart || isEnd) {
            className += " bg-indigo-600 text-white";
          } else if (isInRange || isInHoverRange) {
            className += " bg-indigo-100 text-indigo-800";
          } else {
            className += " text-gray-700 hover:bg-gray-100";
          }
          
          return (
            <button
              key={day}
              onClick={() => handleDateClick(dateString)}
              onMouseEnter={() => handleDateHover(dateString)}
              disabled={isDisabled}
              className={className}
              title={isOccupied ? "Jour occupé" : ""}
            >
              {day}
              {/* Indicateur de début */}
              {isStart && !isSingleDay() && (
                <div className="absolute -top-1 -left-1 w-3 h-3 bg-indigo-600 rounded-full border-2 border-white"></div>
              )}
              {/* Indicateur de fin */}
              {isEnd && !isSingleDay() && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-600 rounded-full border-2 border-white"></div>
              )}
              {/* Indicateur pour jour unique */}
              {isStart && isSingleDay() && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-600 rounded-full border-2 border-white"></div>
              )}
              {/* Indicateur pour jour occupé */}
              {isOccupied && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Boutons d'action */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <button
            onClick={() => onDateRangeSelect('', '')}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            Réinitialiser
          </button>
          {startDate && !endDate && (
            <button
              onClick={() => onDateRangeSelect(startDate, startDate)}
              className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-md transition-colors"
            >
              Jour unique
            </button>
          )}
        </div>
        
        {/* Légende */}
        <div className="flex items-center space-x-4 text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-indigo-600 rounded-full"></div>
            <span>Plage</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-green-600 rounded-full"></div>
            <span>Jour unique</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span>Occupé</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar; 